import { match } from 'ts-pattern'

import type { ConductorTask } from 'Automations/nodeDefinitions/conductor'
import type { SimulationWorkflow } from '../../simulation.types'

/**
 * Picks the task list out of a stored `workflowDefinition`.
 *
 * The backend stores this as opaque JSON and hands it back as
 * `Record<string, any>`, so nothing guarantees the shape - it could have been
 * written by an older version of the editor, or be missing entirely. We check
 * only the fields the walk actually reads and return undefined otherwise, so a
 * bad definition means "we can't simulate this" rather than a crash.
 */
export const parseConductorWorkflow = (
  workflowDefinition: unknown
): SimulationWorkflow | undefined => {
  if (!isRecord(workflowDefinition)) return undefined
  if (!isTaskList(workflowDefinition.tasks)) return undefined

  return { tasks: workflowDefinition.tasks as ConductorTask[] }
}

const isTaskList = (value: unknown): boolean =>
  Array.isArray(value) && value.every(isConductorTask)

const isConductorTask = (value: unknown): boolean => {
  if (!isRecord(value)) return false

  const hasCommonFields =
    typeof value.type === 'string' &&
    typeof value.name === 'string' &&
    typeof value.taskReferenceName === 'string' &&
    isRecord(value.inputParameters)

  if (!hasCommonFields) return false

  return match(value.type)
    .with(
      'FORK_JOIN',
      () => Array.isArray(value.forkTasks) && value.forkTasks.every(isTaskList)
    )
    .with(
      'SWITCH',
      () =>
        typeof value.expression === 'string' &&
        isRecord(value.decisionCases) &&
        Object.values(value.decisionCases).every(isTaskList) &&
        isTaskList(value.defaultCase)
    )
    .with('DO_WHILE', () => isTaskList(value.loopOver))
    .otherwise(() => true)
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
